/**
 * @file Job.jsx
 * @description Job component for displaying and managing job table.
 * @component Job
 * @exports Job - Main component for displaying and managing job table.
 */

import { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid2";
import Stack from '@mui/material/Stack';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import { TextField, Button, Alert } from "@mui/material";
import { getComparator, handleSort } from "../../../hooks/useTable.js";
import { getIvrJob, deleteIvrJob, cancelIvrJob } from "../../../services/ivrModule/jobServices.js";
import { AUTO_COMPLETE, VARIANTS, BUTTON_COLORS, STATUS, SORT_ORDERS, SIZES } from "../../../common/constants/commonConstants.js";
import { JOB_STRINGS, JOB_KEYS, JOB_STATUS, JOB_ACTIONS, JOB_MESSAGES, JOB_ERRORS, JOB_TABLE_COLUMNS } from "../../../common/constants/ivrModule/jobConstants.js";
import JobTable from "./JobTable.jsx";
import CancelDeleteJobDialog from "./CancelDeleteJobDialog.jsx";
import ProgressOverlay from "../../../common/components/ProgressOverlay.jsx";

const Job = () => {
    const [page, setPage] = useState(0);
    const [ivrJobs, setIvrJobs] = useState([]);
    const [validJobs, setValidJobs] = useState([]);
    const [popupOpen, setPopupOpen] = useState(false);
    const [loadingJob, setLoadingJob] = useState(true);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedIds, setSelectedIds] = useState([]);
    const [popupAction, setPopupAction] = useState(null);
    const [popupMessage, setPopupMessage] = useState("");
    const [searchIvrJob, setSearchIvrJob] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [popupLoading, setPopupLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [order, setOrder] = useState(SORT_ORDERS.ASCENDING);
    const [orderBy, setOrderBy] = useState(JOB_KEYS.JOB_NAME);

    useEffect(() => { fetchJob(); }, []);

    useEffect(() => {
        setSelectedIds([]);
    }, [rowsPerPage]);

    useEffect(() => {
        setPage(0);
    }, [searchIvrJob, statusFilter]);

    const fetchJob = async () => {
        try {
            setLoadingJob(true);
            setIvrJobs(await getIvrJob());
        }
        catch (error) {
            console.error(JOB_ERRORS.FETCH_JOB_ERROR, error);
        }
        finally {
            setLoadingJob(false);
        }
    };

    const statusOptions = useMemo(() => {
        const allStatuses = ivrJobs.map(j => j.status).filter(Boolean);
        return Array.from(new Set(allStatuses));
    }, [ivrJobs]);

    const { jobsToDisplay, filteredJobLength } = useMemo(() => {
        let filteredJob = ivrJobs.filter((ivrJob) =>
            ivrJob.id.trim().toLowerCase().includes(searchIvrJob.toLowerCase())
        );
        if (statusFilter) {
            filteredJob = filteredJob.filter((job) => job.status === statusFilter);
        }
        const sortedJobs = filteredJob.sort(getComparator(order, orderBy));
        return {
            jobsToDisplay: sortedJobs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
            filteredJobLength: filteredJob.length,
        };
    }, [ivrJobs, searchIvrJob, statusFilter, order, orderBy, page, rowsPerPage]);

    const handleRequestSort = (property) => {
        handleSort(order, orderBy, property, setOrder, setOrderBy);
    };

    const handleDelete = async () => {
        setPopupAction(JOB_ACTIONS.DELETE);
        if (selectedIds.length === 0) return;
        const selectedJobs = ivrJobs.filter(job => selectedIds.includes(job.id));
        const jobsToDelete = selectedJobs.filter(job => job.status === JOB_STATUS.CANCELLED || job.status === JOB_STATUS.CLOSED);
        if (jobsToDelete.length > 0) {
            setPopupMessage(
                `${jobsToDelete.length} ${JOB_STRINGS.OUT_OF} ${selectedJobs.length} ${JOB_STRINGS.JOBS_SELECTED} ${JOB_STRINGS.BE_DELETED}`
            );
            setValidJobs(jobsToDelete);
        }
        else {
            setPopupMessage(`${JOB_STRINGS.NONE_JOBS_SELECTED} ${JOB_STRINGS.DELETABLE_STATUS}`);
        }
        setPopupOpen(true);
    };

    const handleCancel = async () => {
        setPopupAction(JOB_ACTIONS.CANCEL);
        if (selectedIds.length === 0) return;
        const selectedJobs = ivrJobs.filter(job => selectedIds.includes(job.id));
        const jobsToCancel = selectedJobs.filter(job =>
            job.status === JOB_STATUS.QUEUED || job.status === JOB_STATUS.ASSIGNED
        );
        if (jobsToCancel.length > 0) {
            setPopupMessage(
                `${jobsToCancel.length} ${JOB_STRINGS.OUT_OF} ${selectedJobs.length} ${JOB_STRINGS.JOBS_SELECTED} ${JOB_STRINGS.BE_CANCELLED}`
            );
            setValidJobs(jobsToCancel);
        }
        else {
            setPopupMessage(`${JOB_STRINGS.NONE_JOBS_SELECTED} ${JOB_STRINGS.CANCELLABLE_STATUS}`);
        }
        setPopupOpen(true);
    };

    const confirmPopupAction = async () => {
        setPopupOpen(false);
        setPopupLoading(true); 
        try {
            if (popupAction === JOB_ACTIONS.DELETE) {
                await Promise.all(validJobs.map(job => deleteIvrJob(job.id)));
                setSuccessMessage(JOB_MESSAGES.JOB_DELETED);
            } else if (popupAction === JOB_ACTIONS.CANCEL) {
                await Promise.all(validJobs.map(job => cancelIvrJob(job.id)));
                setSuccessMessage(JOB_MESSAGES.JOB_CANCELLED);
            }
            setSelectedIds([]);
            await fetchJob();
        } catch (error) {
            console.error(`${popupAction} failed:`, error);
            setSuccessMessage(`Failed to ${popupAction} job(s). Please try again.`);
        } finally {
            setPopupLoading(false);
            setPopupAction(null);
            setValidJobs([]);
            setTimeout(() => setSuccessMessage(""), 3000);
        }
    };

    return (
        <>
            {popupLoading && (
				<>
					<ProgressOverlay />
				</>
			)}
            {successMessage &&
                <Alert severity={STATUS.SUCCESS} sx={{ mb: 2, justifyContent: "center" }}>
                    {successMessage}
                </Alert>
            }
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={SIZES.GROW}>
                    <TextField
                        fullWidth
                        size={SIZES.SMALL}
                        value={searchIvrJob}
                        variant={VARIANTS.OUTLINED}
                        label={JOB_STRINGS.SEARCH_JOB}
                        autoComplete={AUTO_COMPLETE.OFF}
                        onChange={e => setSearchIvrJob(e.target.value)}
                    />
                </Grid>
                <Grid size={SIZES.AUTO}>
                    <Stack spacing={2} direction="row">
                        <Button
                            onClick={handleCancel}
                            startIcon={<ClearIcon />}
                            variant={VARIANTS.CONTAINED}
                            disabled={selectedIds.length === 0}
                            color={selectedIds.length === 0 ? BUTTON_COLORS.SECONDARY : BUTTON_COLORS.ERROR}
                        >
                            {JOB_STRINGS.CANCEL_JOB}
                        </Button>
                        <Button
                            onClick={handleDelete}
                            startIcon={<DeleteIcon />}
                            variant={VARIANTS.CONTAINED}
                            disabled={selectedIds.length === 0}
                            color={selectedIds.length === 0 ? BUTTON_COLORS.SECONDARY : BUTTON_COLORS.ERROR}
                        >
                            {JOB_STRINGS.DELETE_JOB}
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
            <JobTable
                page={page}
                order={order}
                setPage={setPage}
                orderBy={orderBy}
                jobs={jobsToDisplay}
                rowsPerPage={rowsPerPage}
                selectedIds={selectedIds}
                isJobLoading={loadingJob}
                columns={JOB_TABLE_COLUMNS}
                statusFilter={statusFilter}
                statusOptions={statusOptions}
                setRowsPerPage={setRowsPerPage}
                onSelectionChange={setSelectedIds}
                filteredJobLength={filteredJobLength}
                handleRequestSort={handleRequestSort}
                onStatusFilterChange={setStatusFilter}
            />
            <CancelDeleteJobDialog
                open={popupOpen}
                action={popupAction}
                message={popupMessage}
                validJobs={validJobs.length}
                onConfirm={confirmPopupAction}
                onClose={() => setPopupOpen(false)}
            />
        </>
    );
};

export default Job;